/**
 * Profile field definitions — the single source of truth shared by the
 * first-login wizard (OnboardingFlow.js) and the post-onboarding editor
 * (ProfileEditor.js). Keeping these here is what stops the two surfaces from
 * drifting (RW-773): add a specialty or a service area once and both forms get
 * it.
 *
 * NOTE on office values: `OFFICE_OPTIONS[].id` (e.g. "new-orleans") is what we
 * persist to users/{uid}.office + .primaryOffice. The richer office catalog
 * (address/phone/coords) lives in src/lib/offices.js; this list is just the
 * pickable {id,label} set for the profile forms.
 */
import { personOfficeId } from './profileOffice';

/* ─── Office ─── */
export const OFFICE_OPTIONS = [
  { id: 'new-orleans', label: 'New Orleans' },
  { id: 'baton-rouge', label: 'Baton Rouge' },
  { id: 'covington', label: 'Covington-Northshore' },
];

/* ─── Specialties ─── */
export const SPECIALTIES = [
  'Residential', 'Luxury', 'First-Time Buyers', 'Relocation', 'Commercial', 'Land', 'Investment',
];

/* ─── Service areas ─── */
// RW-3849: the West Bank, St. Bernard and river-parish towns and the
// Mississippi coast Rêve closes deals in were missing, so an agent working
// them couldn't say so. A new group is a new key in the same users/{uid}
// serviceAreas map — no stored value changes.
export const SERVICE_AREA_GROUPS = [
  {
    key: 'newOrleans', label: 'New Orleans',
    areas: ['Uptown', 'Garden District', 'Audubon', 'Riverside', 'Central City', 'Warehouse District', 'French Quarter', 'Marigny', 'Bywater', 'Tremé', 'Mid-City', 'Lakeview', 'Gentilly', 'Algiers', 'East New Orleans', 'Metairie', 'Kenner', 'Harahan', 'River Ridge', 'Jefferson', 'Gretna', 'Chalmette', 'Arabi', 'Destrehan'],
  },
  {
    key: 'northshore', label: 'Northshore',
    areas: ['Covington', 'Mandeville', 'Madisonville', 'Abita Springs', 'Slidell', 'Lacombe', 'Folsom', 'Bush', 'Bogalusa', 'Hammond', 'Ponchatoula', 'St. Tammany Parish'],
  },
  {
    key: 'batonRouge', label: 'Baton Rouge',
    areas: ['Downtown Baton Rouge', 'Garden District', 'Bocage', 'Broadmoor', 'Sherwood Forest', 'Zachary', 'Central', 'Prairieville', 'Denham Springs', 'Gonzales', 'Shenandoah', 'Brusly', 'Port Allen', 'Baker', 'Livingston Parish', 'Ascension Parish'],
  },
  {
    key: 'mississippiCoast', label: 'Mississippi Gulf Coast',
    areas: ['Bay St. Louis'],
  },
];

/** The `serviceAreas` map shape persisted on the user doc, keyed by group. */
export const SERVICE_AREA_KEYS = SERVICE_AREA_GROUPS.map((g) => g.key);

/** A fresh, empty `serviceAreas` object: { newOrleans: [], northshore: [], …one empty array per group }. */
export function emptyServiceAreas() {
  return SERVICE_AREA_KEYS.reduce((acc, k) => { acc[k] = []; return acc; }, {});
}

/**
 * Coerce an arbitrary stored value into the canonical serviceAreas shape —
 * every group key present with an array value. Tolerates undefined / partial
 * objects loaded from older docs.
 */
export function normalizeServiceAreas(value) {
  const base = emptyServiceAreas();
  if (value && typeof value === 'object') {
    for (const k of SERVICE_AREA_KEYS) {
      if (Array.isArray(value[k])) base[k] = value[k];
    }
  }
  return base;
}

/**
 * Flatten any stored serviceAreas value into one deduped string array for
 * display and search. Tolerates the canonical group map, legacy flat arrays
 * from pre-RW-773 docs, and missing/malformed values (RW-1361: the map shape
 * crashed array-assuming call sites on /people).
 */
export function flattenServiceAreas(value) {
  let areas;
  if (Array.isArray(value)) {
    areas = value;
  } else if (value && typeof value === 'object') {
    areas = SERVICE_AREA_KEYS.flatMap((k) => (Array.isArray(value[k]) ? value[k] : []));
  } else {
    areas = [];
  }
  return [...new Set(areas.filter((a) => typeof a === 'string' && a))];
}

/** Max length for the profile bio (shared cap so the wizard and editor agree). */
export const BIO_MAX = 280;

/** Toggle an item in/out of a string array (immutable). */
export function toggleItem(arr, item) {
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

/* ─── Completeness (RW-3530) ─── */

/** The wizard's phone rule (step 2): at least 7 characters once trimmed. */
export function hasProfilePhone(phone) {
  return String(phone || '').trim().length >= 7;
}

/**
 * RW-3530 — what a profile is missing, worked out from the profile itself.
 * The directory's "Profile incomplete" chip read two flags instead
 * (`onboardingComplete` / `profileComplete`) that nothing sets on profiles
 * finished any other way than the first-run wizard — so Clint's profile, with
 * a photo, title, office, specialties, phone and email, said "Profile
 * incomplete" beside its own "Fully Onboarded" badge, as did 15 of 47 cards.
 * The phone rule is the wizard's own (hasProfilePhone); the office is the one
 * every Company screen shows (personOfficeId). Returns labels, [] = complete.
 */
export function profileMissingFields(person) {
  const p = person || {};
  const missing = [];
  if (!(p.photoURL || p.avatar)) missing.push('photo');
  if (!String(p.title || '').trim()) missing.push('title');
  if (!personOfficeId(p)) missing.push('office');
  if (!hasProfilePhone(p.phone) && !hasProfilePhone(p.mobile)) missing.push('phone');
  if (!(Array.isArray(p.specialties) && p.specialties.length > 0)) missing.push('specialty');
  return missing;
}
